// src/components/Profiles/UserSettings.js
import React, { useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UserContext } from '../../contexts/UserContext';
import { auth } from '../../services/firebase';
import { signOut } from 'firebase/auth';
import '../../styles/Settings.css';

const UserSettings = () => {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('account');

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Handle Digital Card settings - go to advanced settings
  const handleDigitalCardSettings = () => {
    navigate('/settings/digital-card');
  };

  // Handle Digital Card Generator - go to card generator
  const handleDigitalCardGenerator = () => {
    navigate('/card-generator');
  };

  if (!user) {
    return (
      <div className="settings-container">
        <h1>User Settings</h1>
        <p>Please log in to access your settings.</p>
        <Link to="/login" className="settings-button">Login</Link>
      </div>
    );
  }

  return (
    <div className="settings-container">
      <h1>User Settings</h1>
      
      {/* Navigation tabs */}
      <div style={{ 
        display: 'flex', 
        gap: '1rem', 
        marginBottom: '2rem',
        borderBottom: '1px solid #e0e0e0',
        paddingBottom: '1rem'
      }}>
        <button
          onClick={() => setActiveSection('account')}
          style={{
            padding: '0.5rem 1rem',
            border: 'none',
            background: activeSection === 'account' ? '#1e40af' : 'transparent',
            color: activeSection === 'account' ? 'white' : '#666',
            borderRadius: '6px',
            cursor: 'pointer',
            fontFamily: 'Georgia, serif'
          }}
        >
          Account
        </button>
        <button
          onClick={() => setActiveSection('digitalcard')}
          style={{
            padding: '0.5rem 1rem',
            border: 'none',
            background: activeSection === 'digitalcard' ? '#1e40af' : 'transparent',
            color: activeSection === 'digitalcard' ? 'white' : '#666',
            borderRadius: '6px',
            cursor: 'pointer',
            fontFamily: 'Georgia, serif'
          }}
        >
          Digital Card
        </button>
        <button
          onClick={() => setActiveSection('support')}
          style={{
            padding: '0.5rem 1rem',
            border: 'none',
            background: activeSection === 'support' ? '#1e40af' : 'transparent',
            color: activeSection === 'support' ? 'white' : '#666',
            borderRadius: '6px',
            cursor: 'pointer',
            fontFamily: 'Georgia, serif'
          }}
        >
          Support
        </button>
      </div>

      {/* Account Section */}
      {activeSection === 'account' && (
        <>
          <div className="settings-section">
            <h2>Account</h2>
            <ul className="settings-list">
              <li>
                <Link to={`/profiles/${user.email?.split('@')[0] || user.uid}`} className="settings-link">
                  View Profile
                </Link>
              </li>
              <li>
                <Link to="/edit-profile" className="settings-link">
                  Edit Profile
                </Link>
              </li>
              <li>
                <Link to="/settings/notifications" className="settings-link">
                  Notifications
                </Link>
              </li>
              <li>
                <Link to="/settings/privacy" className="settings-link">
                  Privacy
                </Link>
              </li>
            </ul>
          </div>
          
          <div className="settings-section">
            <h2>General</h2>
            <ul className="settings-list">
              <li>
                <Link to="/settings/appearance" className="settings-link">
                  Appearance
                </Link>
              </li>
              <li>
                <Link to="/settings/language" className="settings-link">
                  Language
                </Link>
              </li>
            </ul>
          </div>
          
          <div className="settings-section">
            <button 
              className="settings-button logout-button"
              onClick={handleLogout}
            >
              Logout
            </button>
          </div>
        </>
      )}

      {/* Digital Card Section */}
      {activeSection === 'digitalcard' && (
        <div className="settings-section">
          <h2>Digital Card</h2>
          <p style={{ color: '#64748b', marginBottom: '1.5rem', fontFamily: 'Georgia, serif' }}>
            Manage your Digital Card preferences and appearance
          </p>
          <ul className="settings-list">
            <li>
              <button 
                onClick={handleDigitalCardGenerator}
                className="settings-link"
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  font: 'inherit',
                  cursor: 'pointer',
                  textAlign: 'left',
                  width: '100%'
                }}
              >
                ✨ Create/Edit Digital Card
              </button>
              <p style={{ fontSize: '14px', color: '#94a3b8', margin: '4px 0 0 0' }}>
                Generate or update your Digital Card design and preferences
              </p>
            </li>
            <li>
              <button 
                onClick={handleDigitalCardSettings}
                className="settings-link"
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  font: 'inherit',
                  cursor: 'pointer',
                  textAlign: 'left',
                  width: '100%'
                }}
              >
                ⚙️ Advanced Digital Card Settings
              </button>
              <p style={{ fontSize: '14px', color: '#94a3b8', margin: '4px 0 0 0' }}>
                Fine-tune signal weights and content filtering preferences
              </p>
            </li>
            <li>
              <Link to={`/profiles/${user.email?.split('@')[0] || user.uid}`} className="settings-link">
                👁️ Preview Digital Card
              </Link>
              <p style={{ fontSize: '14px', color: '#94a3b8', margin: '4px 0 0 0' }}>
                See how your Digital Card appears to others
              </p>
            </li>
          </ul>
        </div>
      )}

      {/* Support Section */}
      {activeSection === 'support' && (
        <div className="settings-section">
          <h2>Support</h2>
          <ul className="settings-list">
            <li>
              <Link to="/help" className="settings-link">
                Help Center
              </Link>
            </li>
            <li>
              <Link to="/about" className="settings-link">
                About Artifex
              </Link>
            </li>
            <li>
              <Link to="/terms" className="settings-link">
                Terms of Service
              </Link>
            </li>
            <li>
              <Link to="/privacy" className="settings-link">
                Privacy Policy
              </Link>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default UserSettings;